export type ShortcutId =
	| 'palette.toggle'
	| 'shortcuts.toggle'
	| 'sidebar.toggle'
	| 'header.toggle'
	| 'focusMode.toggle'
	| 'table.selectAll'
	| 'table.toggleHoveredRow'
	| 'table.openHoveredRow'
	| 'table.openActiveRow'
	| 'table.clearActiveOrSelection'
	| 'create.open'
	| 'search.focus'
	| 'auth.logout'
	| 'shortcuts.toggleAlt'
	| 'sidebar.toggleAlt'

export interface ShortcutDefinition {
	id: ShortcutId
	label: string
	/** Hyphen-delimited binding fed to shortcut() for display, e.g. 'Mod-K'. */
	keys: string
	/** Option remaps `key` on macOS, so a binding like Alt-Z must match by physical key code. */
	matchByCode?: boolean
	/** Predates this registry, accepting Cmd or Ctrl on any platform rather than the platform-specific key. */
	looseModifier?: boolean
}

/** Single source of truth for the shortcuts already shipped before 0051: what each one is bound to and called. */
export const SHORTCUT_REGISTRY: Record<ShortcutId, ShortcutDefinition> = {
	'palette.toggle': { id: 'palette.toggle', label: 'Search and run commands', keys: 'Mod-K' },
	'shortcuts.toggle': { id: 'shortcuts.toggle', label: 'Keyboard shortcuts', keys: 'Mod-/' },
	'sidebar.toggle': { id: 'sidebar.toggle', label: 'Show or hide sidebar', keys: 'Mod-B', looseModifier: true },
	'header.toggle': { id: 'header.toggle', label: 'Show or hide header', keys: 'Mod-J', looseModifier: true },
	'focusMode.toggle': { id: 'focusMode.toggle', label: 'Focus mode', keys: 'Alt-Z', matchByCode: true },
	'table.selectAll': { id: 'table.selectAll', label: 'Select all rows', keys: 'Mod-A', looseModifier: true },
	'table.toggleHoveredRow': { id: 'table.toggleHoveredRow', label: 'Select or clear the hovered row', keys: 'X' },
	'table.openHoveredRow': {
		id: 'table.openHoveredRow',
		label: 'Open the hovered row',
		keys: 'Space',
		matchByCode: true,
	},
	'table.openActiveRow': { id: 'table.openActiveRow', label: 'Open the active row', keys: 'Enter' },
	'table.clearActiveOrSelection': {
		id: 'table.clearActiveOrSelection',
		label: 'Clear the selection, then the active row',
		keys: 'Escape',
	},
	'create.open': { id: 'create.open', label: 'Create', keys: 'C' },
	'search.focus': { id: 'search.focus', label: "Focus the page's main search", keys: '/' },
	'auth.logout': { id: 'auth.logout', label: 'Log out', keys: 'Alt-Shift-Q', matchByCode: true },
	// `?` is the RFC's single-key alternate for Mod+/, same command, same handler as shortcuts.toggle.
	'shortcuts.toggleAlt': { id: 'shortcuts.toggleAlt', label: 'Keyboard shortcuts', keys: '?' },
	'sidebar.toggleAlt': { id: 'sidebar.toggleAlt', label: 'Show or hide sidebar', keys: '[' },
}
